'use-client';
import * as React from 'react';
import classNames from 'clsx';
import {
  formatClassNames,
  getDataAttributes,
  isBrowser,
} from '@wix/editor-elements-common-utils';
import { getComponentProps } from '@wix/thunderbolt-elements/src/components/ConsentPolicyWrapper/viewer/utils';
import type {
  GoogleMapLegacyProps,
  IGoogleMapsImperativeActions,
} from '../GoogleMap.types';

import semanticClassNames from '../GoogleMap.semanticClassNames';
import { useGoogleIFrame } from './hooks';
import { SKIN_BORDERS } from './skinParts';

import style from './style/GoogleMapLegacy.module.scss';

import googleMapHtml from './assets/googleMap.html?resource';
import googleMapsScriptUrl from './assets/google-map.min.js?resource';

const getGoogleMapUrl = (
  urlQueries: GoogleMapLegacyProps['urlQueries'],
  hasFocus?: boolean,
) => {
  const additionalParams = new URLSearchParams({
    googleMapsScriptPath: new URL(googleMapsScriptUrl).pathname,
  });
  if (isBrowser()) {
    additionalParams.append('origin', window.origin);
  }
  if (hasFocus !== undefined) {
    additionalParams.append('hasFocus', hasFocus.toString());
  }
  return `${googleMapHtml}?${urlQueries}&${additionalParams}`;
};

const GoogleMapLegacy: React.ForwardRefRenderFunction<
  IGoogleMapsImperativeActions,
  GoogleMapLegacyProps
> = (props, forwardRef) => {
  const {
    id,
    className,
    customClassNames = [],
    skin,
    urlQueries,
    mapData,
    translations,
    isConsentPolicyActive,
    onUpdateCenter,
    onUpdateZoom,
    onMarkerClicked,
    onMapClicked,
    onMouseEnter,
    onMouseLeave,
    isEditorMode,
    language,
    onMapMount,
    lang,
    hasFocus,
  } = props;
  const SkinBorders = SKIN_BORDERS[skin];

  const url = getGoogleMapUrl(urlQueries, hasFocus);

  const [render, setRender] = React.useState(false);
  const [ref] = useGoogleIFrame(
    forwardRef,
    { mapData, isEditorMode, language },
    {
      onUpdateCenter,
      onUpdateZoom,
      onMarkerClicked,
      onMapClicked,
    },
  );

  React.useEffect(() => {
    setRender(true);
  }, [url]);

  React.useEffect(() => {
    onMapMount?.();
  }, [onMapMount]);

  const title = translations.title;

  const baseComponentProps = getComponentProps(isConsentPolicyActive, {
    id,
    className: classNames(
      style[skin],
      style.wixIframe,
      className,
      formatClassNames(semanticClassNames.root, ...customClassNames),
    ),
    ...getDataAttributes(props),
    lang,
  });

  return (
    <div {...baseComponentProps}>
      {render && (
        <wix-iframe data-src={url}>
          <SkinBorders />
          <div
            id={`mapContainer_${id}`}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            className={style.mapContainer}
          >
            <iframe
              ref={ref}
              title={title}
              data-src={url}
              width="100%"
              height="100%"
              frameBorder="0"
              scrolling="no"
              allowFullScreen
            />
          </div>
        </wix-iframe>
      )}
    </div>
  );
};

export default React.forwardRef(GoogleMapLegacy);
